/**
 * AdminBusinesses Component
 * Page for managing all salons/businesses with CRUD operations
 */

import React, { Component } from 'react';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';
import MultiLocationLinkModal from './MultiLocationLinkModal';
import './Admin.css';

class AdminBusinesses extends Component {
    constructor(props) {
        super(props);
        this.state = {
            businesses: [],
            loading: true,
            error: null,
            search: '',
            page: 1,
            limit: 20,
            totalPages: 1,
            totalCount: 0,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            claimedFilter: 'all',
            // Import modal state
            showImportModal: false,
            importLimit: '',
            importFileData: null,
            importFileName: '',
            importFileCount: 0,
            importDragging: false,
            importLoading: false,
            importResult: null,
            importError: null,
            // Enrichment state
            selectedSalonIds: [],
            showEnrichModal: false,
            enrichMode: 'batch',
            enrichLimit: '5',
            enrichLoading: false,
            enrichResult: null,
            enrichError: null,
            showMultiLocationModal: false
        };
        this.searchTimeout = null;
    }

    componentDidMount() {
        this.fetchBusinesses();
    }

    fetchBusinesses = async () => {
        const { page, limit, search, sortBy, sortOrder, claimedFilter } = this.state;
        this.setState({ loading: true });

        try {
            const response = await adminService.getSalons({
                page,
                limit,
                search,
                sortBy,
                sortOrder,
                claimedFilter
            });

            if (response.statusCode === 1) {
                this.setState({
                    businesses: response.responseData.salons || [],
                    totalPages: response.responseData.pagination?.pages || 1,
                    totalCount: response.responseData.pagination?.total || 0,
                    loading: false
                });
            } else {
                this.setState({
                    error: 'Failed to load businesses',
                    loading: false
                });
            }
        } catch (error) {
            console.error('Error fetching businesses:', error);
            this.setState({
                error: 'Failed to load businesses',
                loading: false
            });
        }
    }

    handleSearchChange = (e) => {
        const search = e.target.value;
        this.setState({ search, page: 1 });

        // Debounce search
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        this.searchTimeout = setTimeout(() => {
            this.fetchBusinesses();
        }, 300);
    }

    handlePageChange = (newPage) => {
        this.setState({ page: newPage }, this.fetchBusinesses);
    }

    handleDelete = async (businessId, businessName) => {
        if (!window.confirm(`Are you sure you want to delete business "${businessName}"? This will also affect all associated professionals and bookings. This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await adminService.deleteSalon(businessId);
            if (response.statusCode === 1) {
                this.fetchBusinesses();
            } else {
                alert('Failed to delete business');
            }
        } catch (error) {
            console.error('Error deleting business:', error);
            alert('Failed to delete business');
        }
    }

    handleClaimedFilterChange = (e) => {
        this.setState({ claimedFilter: e.target.value, page: 1 }, this.fetchBusinesses);
    }

    handleFileDrop = (e) => {
        e.preventDefault();
        this.setState({ importDragging: false });
        const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
        if (!file) return;
        if (!file.name.endsWith('.json')) {
            this.setState({ importError: 'Please upload a .json file' });
            return;
        }
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = JSON.parse(event.target.result);
                if (!Array.isArray(data)) {
                    this.setState({ importError: 'JSON file must contain an array of businesses' });
                    return;
                }
                this.setState({
                    importFileData: data,
                    importFileName: file.name,
                    importFileCount: data.length,
                    importError: null
                });
            } catch (err) {
                this.setState({ importError: 'Invalid JSON file: ' + err.message });
            }
        };
        reader.readAsText(file);
    }

    handleDryRun = async () => {
        const { importFileData } = this.state;
        if (!importFileData) {
            this.setState({ importError: 'Please upload a JSON file first' });
            return;
        }
        this.setState({ importLoading: true, importError: null, importResult: null });
        try {
            const response = await adminService.importBusinessesDryRun(importFileData);
            if (response.statusCode === 1) {
                this.setState({ importResult: response.responseData, importLoading: false });
            } else {
                this.setState({ importError: 'Dry run failed', importLoading: false });
            }
        } catch (error) {
            this.setState({ importError: error.toString(), importLoading: false });
        }
    }

    handleImport = async () => {
        const { importFileData, importLimit } = this.state;
        if (!importFileData) {
            this.setState({ importError: 'Please upload a JSON file first' });
            return;
        }
        const limit = importLimit ? parseInt(importLimit, 10) : 0;
        const msg = limit > 0
            ? `This will import ${limit} businesses. Are you sure?`
            : 'This will import ALL relevant businesses. Are you sure?';
        if (!window.confirm(msg)) return;

        this.setState({ importLoading: true, importError: null, importResult: null });
        try {
            const response = await adminService.importBusinesses(importFileData, limit);
            if (response.statusCode === 1) {
                this.setState({ importResult: response.responseData, importLoading: false });
                this.fetchBusinesses();
            } else {
                this.setState({ importError: 'Import failed', importLoading: false });
            }
        } catch (error) {
            this.setState({ importError: error.toString(), importLoading: false });
        }
    }

    // Enrichment handlers
    handleSelectSalon = (salonId) => {
        this.setState(prevState => {
            const selected = prevState.selectedSalonIds.includes(salonId)
                ? prevState.selectedSalonIds.filter(id => id !== salonId)
                : [...prevState.selectedSalonIds, salonId];
            return { selectedSalonIds: selected };
        });
    }

    handleSelectAllOnPage = () => {
        const unclaimed = this.state.businesses.filter(b => b.isClaimed === false);
        const unclaimedIds = unclaimed.map(b => b._id);
        const allSelected = unclaimedIds.every(id => this.state.selectedSalonIds.includes(id));

        if (allSelected) {
            this.setState(prevState => ({
                selectedSalonIds: prevState.selectedSalonIds.filter(id => !unclaimedIds.includes(id))
            }));
        } else {
            this.setState(prevState => ({
                selectedSalonIds: [...new Set([...prevState.selectedSalonIds, ...unclaimedIds])]
            }));
        }
    }

    handleEnrich = async () => {
        const { enrichMode, enrichLimit, selectedSalonIds } = this.state;
        const salonIds = enrichMode === 'selected' ? selectedSalonIds : [];
        const limit = enrichMode === 'batch' ? parseInt(enrichLimit, 10) || 10 : 0;
        const count = enrichMode === 'selected' ? selectedSalonIds.length : limit;

        if (!window.confirm(`Enrich ${count} businesses with Google Places data? This will make API calls to Google.`)) return;

        this.setState({ enrichLoading: true, enrichError: null, enrichResult: null });
        try {
            const response = await adminService.enrichWithGooglePlaces(salonIds, limit);
            if (response.statusCode === 1) {
                this.setState({ enrichResult: response.responseData, enrichLoading: false });
                this.fetchBusinesses();
            } else {
                this.setState({ enrichError: 'Enrichment failed', enrichLoading: false });
            }
        } catch (error) {
            this.setState({ enrichError: error.toString(), enrichLoading: false });
        }
    }

    getInitials = (name) => {
        if (!name) return 'B';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }

    formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    }

    getLocation = (business) => {
        const parts = [];
        if (business.city) parts.push(business.city);
        if (business.country) {
            if (typeof business.country === 'object') {
                parts.push(business.country.englishName || business.country.name);
            } else {
                parts.push(business.country);
            }
        }
        return parts.length > 0 ? parts.join(', ') : 'N/A';
    }

    render() {
        const { businesses, loading, error, search, page, totalPages, totalCount,
            claimedFilter, showImportModal, importLimit, importFileData, importFileName, importFileCount,
            importDragging, importLoading, importResult, importError,
            selectedSalonIds, showEnrichModal, enrichMode, enrichLimit,
            enrichLoading, enrichResult, enrichError } = this.state;
        const { showMultiLocationModal } = this.state;

        return (
            <AdminWrapper>
                <div className="admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h1 className="admin-page-title">Businesses</h1>
                        <p className="admin-page-subtitle">Manage all salons and beauty businesses</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            className="admin-btn admin-btn-secondary"
                            onClick={() => this.setState({ showMultiLocationModal: true })}
                        >
                            Link or unlink locations
                        </button>
                        {selectedSalonIds.length > 0 && (
                            <button
                                className="admin-btn admin-btn-primary"
                                onClick={() => this.setState({
                                    showEnrichModal: true, enrichMode: 'selected',
                                    enrichResult: null, enrichError: null
                                })}
                            >
                                Enrich Selected ({selectedSalonIds.length})
                            </button>
                        )}
                        <button
                            className="admin-btn admin-btn-secondary"
                            onClick={() => this.setState({
                                showEnrichModal: true, enrichMode: 'batch',
                                enrichResult: null, enrichError: null, enrichLimit: '5'
                            })}
                        >
                            Enrich with Google
                        </button>
                        <button
                            className="admin-btn admin-btn-primary"
                            onClick={() => this.setState({ showImportModal: true, importResult: null, importError: null, importFileData: null, importFileName: '', importFileCount: 0, importLimit: '' })}
                        >
                            Import Businesses
                        </button>
                    </div>
                </div>

                {showMultiLocationModal && <MultiLocationLinkModal onClose={() => this.setState({ showMultiLocationModal: false })} />}

                {/* Import Modal */}
                {showImportModal && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', zIndex: 9999
                    }}
                        onClick={() => !importLoading && this.setState({ showImportModal: false })}
                    >
                        <div style={{
                            background: '#fff', borderRadius: '12px', padding: '32px',
                            maxWidth: '520px', width: '90%', maxHeight: '80vh', overflow: 'auto'
                        }}
                            onClick={e => e.stopPropagation()}
                        >
                            <h2 style={{ margin: '0 0 8px 0', fontSize: '20px' }}>Import Enriched Businesses</h2>
                            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 24px 0' }}>
                                Import businesses from the enriched dataset as unclaimed salon profiles.
                            </p>

                            {!importResult && (
                                <>
                                    {/* Drag & Drop Zone */}
                                    <div
                                        onDragOver={e => { e.preventDefault(); this.setState({ importDragging: true }); }}
                                        onDragLeave={() => this.setState({ importDragging: false })}
                                        onDrop={this.handleFileDrop}
                                        onClick={() => this.fileInput && this.fileInput.click()}
                                        style={{
                                            border: `2px dashed ${importDragging ? '#4CAF50' : importFileData ? '#4CAF50' : '#ddd'}`,
                                            borderRadius: '12px',
                                            padding: '32px 20px',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            marginBottom: '16px',
                                            background: importDragging ? '#f0fdf0' : importFileData ? '#f8fdf8' : '#fafafa',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <input
                                            ref={el => this.fileInput = el}
                                            type="file"
                                            accept=".json"
                                            onChange={this.handleFileDrop}
                                            style={{ display: 'none' }}
                                        />
                                        {importFileData ? (
                                            <div>
                                                <div style={{ fontSize: '24px', marginBottom: '8px' }}>&#10003;</div>
                                                <div style={{ fontWeight: '500', fontSize: '14px' }}>{importFileName}</div>
                                                <div style={{ color: '#666', fontSize: '13px', marginTop: '4px' }}>
                                                    {importFileCount.toLocaleString()} businesses loaded
                                                </div>
                                                <div style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                                                    Drop another file to replace
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.4 }}>&#128449;</div>
                                                <div style={{ fontWeight: '500', fontSize: '14px', color: '#666' }}>
                                                    Drag & drop your JSON file here
                                                </div>
                                                <div style={{ color: '#999', fontSize: '12px', marginTop: '4px' }}>
                                                    or click to browse
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Limit Input */}
                                    {importFileData && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                                                Number to import
                                            </label>
                                            <input
                                                type="number"
                                                value={importLimit}
                                                onChange={e => this.setState({ importLimit: e.target.value })}
                                                placeholder={`Leave empty for all (${importFileCount.toLocaleString()} in file)`}
                                                min="1"
                                                style={{
                                                    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                                                    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'
                                                }}
                                            />
                                            <p style={{ color: '#999', fontSize: '12px', margin: '4px 0 0' }}>
                                                Set a number to import a subset, or leave empty for all relevant businesses.
                                            </p>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <button
                                            className="admin-btn admin-btn-secondary"
                                            onClick={this.handleDryRun}
                                            disabled={importLoading || !importFileData}
                                            style={{ flex: 1 }}
                                        >
                                            {importLoading ? 'Running...' : 'Preview (Dry Run)'}
                                        </button>
                                        <button
                                            className="admin-btn admin-btn-primary"
                                            onClick={this.handleImport}
                                            disabled={importLoading || !importFileData}
                                            style={{ flex: 1 }}
                                        >
                                            {importLoading ? 'Importing...' : importLimit ? `Import ${importLimit}` : 'Import All'}
                                        </button>
                                    </div>
                                </>
                            )}

                            {importError && (
                                <div style={{
                                    marginTop: '16px', padding: '12px', background: '#ffebee',
                                    borderRadius: '8px', color: '#c62828', fontSize: '14px'
                                }}>
                                    {importError}
                                </div>
                            )}

                            {importResult && (
                                <div style={{ marginTop: '16px' }}>
                                    <div style={{
                                        padding: '12px', background: '#e8f5e9',
                                        borderRadius: '8px', color: '#2e7d32', fontSize: '14px', marginBottom: '16px'
                                    }}>
                                        {importResult.message}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                                        <div>Total in file: <strong>{importResult.total}</strong></div>
                                        <div>Relevant: <strong>{importResult.relevant}</strong></div>
                                        <div>Skipped (industry): <strong>{importResult.skippedIndustry}</strong></div>
                                        <div>Skipped (duplicate): <strong>{importResult.skippedDuplicate || 0}</strong></div>
                                        {importResult.imported !== undefined && (
                                            <div>Imported: <strong>{importResult.imported}</strong></div>
                                        )}
                                        {importResult.geocodeFailed !== undefined && (
                                            <div>Geocode failed: <strong>{importResult.geocodeFailed}</strong></div>
                                        )}
                                    </div>
                                    {importResult.businessTypeBreakdown && (
                                        <div style={{ marginTop: '12px' }}>
                                            <div style={{ fontWeight: '500', marginBottom: '4px', fontSize: '13px' }}>By type:</div>
                                            {Object.entries(importResult.businessTypeBreakdown).map(([type, count]) => (
                                                <div key={type} style={{ fontSize: '12px', color: '#666' }}>
                                                    {type}: {count}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <button
                                        className="admin-btn admin-btn-secondary"
                                        onClick={() => this.setState({ showImportModal: false })}
                                        style={{ marginTop: '16px', width: '100%' }}
                                    >
                                        Close
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Enrich Modal */}
                {showEnrichModal && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', zIndex: 9999
                    }}
                        onClick={() => !enrichLoading && this.setState({ showEnrichModal: false })}
                    >
                        <div style={{
                            background: '#fff', borderRadius: '12px', padding: '32px',
                            maxWidth: '520px', width: '90%', maxHeight: '80vh', overflow: 'auto'
                        }}
                            onClick={e => e.stopPropagation()}
                        >
                            <h2 style={{ margin: '0 0 8px 0', fontSize: '20px' }}>Enrich with Google Places</h2>
                            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 24px 0' }}>
                                Fetch opening hours, photos, ratings, phone & website from Google Places API.
                            </p>

                            {!enrichResult && (
                                <>
                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                                            {enrichMode === 'selected'
                                                ? `${selectedSalonIds.length} businesses selected`
                                                : 'Number to enrich'}
                                        </label>
                                        {enrichMode === 'batch' && (
                                            <input
                                                type="number"
                                                value={enrichLimit}
                                                onChange={e => this.setState({ enrichLimit: e.target.value })}
                                                placeholder="Number of businesses"
                                                min="1"
                                                max="500"
                                                style={{
                                                    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                                                    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'
                                                }}
                                            />
                                        )}
                                        <p style={{ color: '#999', fontSize: '12px', margin: '4px 0 0' }}>
                                            Start with 5-10 to test. Each business uses ~3 Google API calls.
                                        </p>
                                    </div>

                                    <button
                                        className="admin-btn admin-btn-primary"
                                        onClick={this.handleEnrich}
                                        disabled={enrichLoading}
                                        style={{ width: '100%' }}
                                    >
                                        {enrichLoading ? 'Enriching...' : 'Start Enrichment'}
                                    </button>
                                </>
                            )}

                            {enrichError && (
                                <div style={{
                                    marginTop: '16px', padding: '12px', background: '#ffebee',
                                    borderRadius: '8px', color: '#c62828', fontSize: '14px'
                                }}>
                                    {enrichError}
                                </div>
                            )}

                            {enrichResult && (
                                <div style={{ marginTop: '16px' }}>
                                    <div style={{
                                        padding: '12px', background: '#e8f5e9',
                                        borderRadius: '8px', color: '#2e7d32', fontSize: '14px', marginBottom: '16px'
                                    }}>
                                        {enrichResult.message}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                                        <div>Total processed: <strong>{enrichResult.total}</strong></div>
                                        <div>Enriched: <strong>{enrichResult.enriched}</strong></div>
                                        <div>Skipped (no match): <strong>{enrichResult.skipped}</strong></div>
                                        <div>Failed: <strong>{enrichResult.failed}</strong></div>
                                    </div>
                                    <button
                                        className="admin-btn admin-btn-secondary"
                                        onClick={() => this.setState({ showEnrichModal: false, selectedSalonIds: [] })}
                                        style={{ marginTop: '16px', width: '100%' }}
                                    >
                                        Close
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="admin-table-container">
                    <div className="admin-table-header">
                        <h3 className="admin-table-title">
                            {totalCount} {totalCount === 1 ? 'Business' : 'Businesses'}
                        </h3>
                        <div className="admin-table-actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <select
                                value={claimedFilter}
                                onChange={this.handleClaimedFilterChange}
                                style={{
                                    padding: '8px 12px', border: '1px solid #ddd',
                                    borderRadius: '8px', fontSize: '14px', background: '#fff'
                                }}
                            >
                                <option value="all">All Businesses</option>
                                <option value="claimed">Claimed Only</option>
                                <option value="unclaimed">Unclaimed Only</option>
                            </select>
                            <input
                                type="text"
                                className="admin-search-input"
                                placeholder="Search by name or location..."
                                value={search}
                                onChange={this.handleSearchChange}
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <div className="admin-loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                            <p style={{ color: '#666' }}>Loading businesses...</p>
                        </div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error}</p>
                            <button className="admin-btn admin-btn-secondary" onClick={this.fetchBusinesses}>
                                Retry
                            </button>
                        </div>
                    ) : businesses.length === 0 ? (
                        <div className="admin-empty-state">
                            <div className="admin-empty-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                    <polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                            </div>
                            <h3 className="admin-empty-title">No businesses found</h3>
                            <p className="admin-empty-text">
                                {search ? 'Try adjusting your search criteria' : 'No businesses have registered yet'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}>
                                            <input type="checkbox" onChange={this.handleSelectAllOnPage} />
                                        </th>
                                        <th>Business</th>
                                        <th>Location</th>
                                        <th>Phone</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {businesses.map(business => (
                                        <tr key={business._id} onClick={() => this.props.history.push(`/beu-admin-x7k9/businesses/${business._id}`)} style={{ cursor: 'pointer' }}>
                                            <td onClick={e => e.stopPropagation()}>
                                                {business.isClaimed === false && (
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedSalonIds.includes(business._id)}
                                                        onChange={() => this.handleSelectSalon(business._id)}
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    {business.image || business.logo ? (
                                                        <img
                                                            src={business.image || business.logo}
                                                            alt={business.name || business.salonName}
                                                            className="admin-table-avatar"
                                                            style={{ borderRadius: '8px' }}
                                                        />
                                                    ) : (
                                                        <div className="admin-table-avatar-placeholder" style={{ borderRadius: '8px' }}>
                                                            {this.getInitials(business.name || business.salonName)}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div style={{ fontWeight: '500' }}>
                                                            {business.name || business.salonName || 'Unknown'}
                                                        </div>
                                                        {business.email && (
                                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                                {business.email}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{this.getLocation(business)}</td>
                                            <td>{business.phoneNumber || business.phone || 'N/A'}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                    <span className={`admin-badge ${business.isShow !== false ? 'admin-badge-success' : 'admin-badge-warning'}`}>
                                                        {business.isShow !== false ? 'Visible' : 'Hidden'}
                                                    </span>
                                                    {business.isClaimed === false && (
                                                        <span className="admin-badge" style={{ background: '#e3f2fd', color: '#1565c0' }}>
                                                            Unclaimed
                                                        </span>
                                                    )}
                                                    {business.enrichmentStatus === 'enriched' && (
                                                        <span className="admin-badge" style={{ background: '#e8f5e9', color: '#2e7d32' }}>
                                                            Enriched
                                                        </span>
                                                    )}
                                                    {business.enrichmentStatus === 'failed' && (
                                                        <span className="admin-badge" style={{ background: '#ffebee', color: '#c62828' }}>
                                                            Enrich Failed
                                                        </span>
                                                    )}
                                                    {business.enrichmentStatus === 'skipped' && (
                                                        <span className="admin-badge" style={{ background: '#fff3e0', color: '#e65100' }}>
                                                            No Match
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{this.formatDate(business.createdAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {totalPages > 1 && (
                                <div className="admin-pagination">
                                    <div className="admin-pagination-info">
                                        Page {page} of {totalPages} ({totalCount} total)
                                    </div>
                                    <div className="admin-pagination-controls">
                                        <button
                                            className="admin-pagination-btn"
                                            onClick={() => this.handlePageChange(page - 1)}
                                            disabled={page === 1}
                                        >
                                            Previous
                                        </button>
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (page <= 3) {
                                                pageNum = i + 1;
                                            } else if (page >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = page - 2 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`admin-pagination-btn ${page === pageNum ? 'active' : ''}`}
                                                    onClick={() => this.handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                        <button
                                            className="admin-pagination-btn"
                                            onClick={() => this.handlePageChange(page + 1)}
                                            disabled={page === totalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </AdminWrapper>
        );
    }
}

export default AdminBusinesses;
